import React from "react";
import { createRoot } from "react-dom/client";


// Async IIFE — top-level await requires ES2022+ which is outside our browser targets
(async () => {
  // Mount points — check DOM first, only download code that's actually needed on this page
  const resolutionSearchEl   = document.getElementById("react-resolution-search");
  const organisationSearchEl = document.getElementById("react-organisation-search");
  const conservationPointsEl = document.getElementById("react-conservation-points");
  const improvementPointsEl  = document.getElementById("react-improvement-points");
  const actionPlanDetailsEl  = document.getElementById("react-action-plan-point-details");
  const actionPlanSummaryEl  = document.getElementById("react-action-plan-summary");
  const stickyCommentSaveEl  = document.getElementById("react-sticky-comment-save");
  const chatbot = document.getElementById("react-chatbot");

  // ── Chatbot ────────────────────────────────────────────────────────────────

  if (chatbot) {
    const { default: HeroChatbot } = await import(
      "./components/react-components/Chatbot/Chatbot"
    );
    createRoot(chatbot).render(<HeroChatbot />);
  }

  // ── Standalone pages ───────────────────────────────────────────────────────
  if (resolutionSearchEl) {
    const { default: ResolutionSearchPage } = await import(
      "./components/react-components/ResolutionSearchPage/ResolutionSearchPage"
    );
    createRoot(resolutionSearchEl).render(<ResolutionSearchPage />);
  }

  if (organisationSearchEl) {
    const { default: OrganisationSearchPage } = await import(
      "./components/react-components/OrganisationSearchPage/OrganisationSearchPage"
    );
    createRoot(organisationSearchEl).render(<OrganisationSearchPage />);
  }

  // ── ArbHelp — import all shared modules together only when any element exists

  const hasArbHelp = conservationPointsEl || improvementPointsEl || actionPlanDetailsEl || actionPlanSummaryEl || stickyCommentSaveEl;

  if (hasArbHelp) {
    const [
      { default: ActionPlanPoints },
      { default: ActionPlanPointDetails },
      { default: ActionPlanSummary },
      { default: StickyCommentSave },
      { TranslationsProvider },
    ] = await Promise.all([
      import("./components/react-components/ArbHelp/ActionPlanPoints"),
      import("./components/react-components/ArbHelp/ActionPlanPointDetails"),
      import("./components/react-components/ArbHelp/ActionPlanSummary"),
      import("./components/react-components/ArbHelp/StickyCommentSave"),
      import("./components/react-components/ArbHelp/translationsContext"),
    ]);

    const getModelData = () =>
      JSON.parse(document.querySelector("#js-react-arbhelp").innerHTML);
    const getTranslations = () =>
      JSON.parse(document.querySelector("#translations").innerHTML);

    if (conservationPointsEl) {
      const modelData    = getModelData();
      const translations = getTranslations();
      const conservationPoints =
        modelData.actionPlanPoints?.filter((p) => p.actionPlanType === 1) || [];
      const { actionPlanPoints, ...rest } = modelData;
      createRoot(conservationPointsEl).render(
        <TranslationsProvider translations={translations}>
          <ActionPlanPoints actionPlanPoints={conservationPoints} {...rest} actionPlanType={1} />
        </TranslationsProvider>
      );
    }

    if (improvementPointsEl) {
      const modelData    = getModelData();
      const translations = getTranslations();
      const improvementPoints =
        modelData.actionPlanPoints?.filter((p) => p.actionPlanType === 2) || [];
      const { actionPlanPoints, ...rest } = modelData;
      createRoot(improvementPointsEl).render(
        <TranslationsProvider translations={translations}>
          <ActionPlanPoints actionPlanPoints={improvementPoints} {...rest} actionPlanType={2} />
        </TranslationsProvider>
      );
    }

    if (actionPlanDetailsEl) {
      const modelData    = getModelData();
      const translations = getTranslations();
      createRoot(actionPlanDetailsEl).render(
        <TranslationsProvider translations={translations}>
          <ActionPlanPointDetails {...modelData} />
        </TranslationsProvider>
      );
    }

    if (actionPlanSummaryEl) {
      const modelData    = getModelData();
      const translations = getTranslations();
      createRoot(actionPlanSummaryEl).render(
        <TranslationsProvider translations={translations}>
          <ActionPlanSummary {...modelData} />
        </TranslationsProvider>
      );
    }

    if (stickyCommentSaveEl) {
      const modelData    = getModelData();
      const translations = getTranslations();
      createRoot(stickyCommentSaveEl).render(
        <TranslationsProvider translations={translations}>
          <StickyCommentSave {...modelData} />
        </TranslationsProvider>
      );
    }
  }
})();